import { useMemo } from 'react'
import Select, { GroupBase, Props, PropsValue } from 'react-select'
import { isLooseOptionSelected, resolveSelectValue, SelectOption } from './reactSelectHelpers'

type LooseSingleValue<Option extends SelectOption> =
  | PropsValue<Option>
  | string
  | number

type AppSelectProps<
  Option extends SelectOption = SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = Omit<Props<Option, IsMulti, Group>, 'value'> & {
  value?: IsMulti extends true ? Props<Option, IsMulti, Group>['value'] : LooseSingleValue<Option>
}

/**
 * Drop-in react-select wrapper with loose value matching (string vs number ids).
 * Use anywhere filter/list lookups build value objects separately from options.
 */
function AppSelect<
  Option extends SelectOption = SelectOption,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: AppSelectProps<Option, IsMulti, Group>) {
  const {
    value,
    options,
    classNamePrefix = 'react-select',
    isOptionSelected,
    isMulti,
    menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined,
    menuPosition = 'fixed',
    menuPlacement = 'auto',
    minMenuHeight = 260,
    maxMenuHeight = 240,
    menuShouldScrollIntoView = false,
    styles,
    ...rest
  } = props

  const resolvedValue = useMemo(() => {
    if (isMulti) return value as PropsValue<Option>
    return resolveSelectValue(
      value as Option | string | number | null | undefined,
      options as readonly Option[] | undefined
    )
  }, [value, options, isMulti])

  const customStyles = useMemo(() => {
    const parentStyles = (styles as any) || {}
    return {
      ...parentStyles,
      menuPortal: (base: any, state: any) => ({
        ...base,
        zIndex: 999999,
        pointerEvents: 'auto',
        ...(parentStyles.menuPortal ? parentStyles.menuPortal(base, state) : {}),
      }),
      menu: (base: any, state: any) => ({
        ...base,
        zIndex: 999999,
        marginBottom: '8px',
        marginTop: '8px',
        ...(parentStyles.menu ? parentStyles.menu(base, state) : {}),
      }),
      menuList: (base: any, state: any) => ({
        ...base,
        maxHeight: base.maxHeight ? Math.min(base.maxHeight, maxMenuHeight) : maxMenuHeight,
        overflowY: 'scroll',
        scrollbarWidth: 'thin',
        scrollbarGutter: 'stable',
        ...(parentStyles.menuList ? parentStyles.menuList(base, state) : {}),
      }),
    }
  }, [styles, maxMenuHeight])

  return (
    <Select
      classNamePrefix={classNamePrefix}
      isOptionSelected={isOptionSelected ?? isLooseOptionSelected}
      isMulti={isMulti}
      menuPortalTarget={menuPortalTarget}
      menuPosition={menuPosition}
      menuPlacement={menuPlacement}
      minMenuHeight={minMenuHeight}
      maxMenuHeight={maxMenuHeight}
      menuShouldScrollIntoView={menuShouldScrollIntoView}
      {...rest}
      styles={customStyles}
      options={options}
      value={resolvedValue}
    />
  )
}

export default AppSelect
